import React, { useEffect, useState } from 'react';
import { formatDate } from '../../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, TextField,
    Stack
} from '@mui/material';
import { RequisitionService } from '../../services/requisition.service';
import { Requisition, REQUISITION_STATUS_LABELS } from '../../interfaces/requisition.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Store } from '../../interfaces/store.interface';
import { ItemService } from '../../services/item.service';
import { Item } from '../../interfaces/item.interface';
import { useToast } from '../../context/ToastContext';
import StockLotPicker from '../common/StockLotPicker';
import SearchableSelect from '../common/SearchableSelect';
import ConfirmDialog from '../common/ConfirmDialog';
import RejectReasonDialog from './RejectReasonDialog';

interface ApprovalDetail {
    id: string;
    approvedQty: number;
    itemId: string;
    storeId: string;
    stockLotId?: string | null;
}

const PendingApprovalRequisitionDetails: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [requisition, setRequisition] = useState<Requisition | null>(null);
    const [loading, setLoading] = useState(true);
    const [storeError, setStoreError] = useState<boolean>(false);
    // Raw text of each approved-quantity box; a line with no entry defaults to its required quantity.
    const [approvedInputs, setApprovedInputs] = useState<{ [key: string]: string }>({});
    const [confirming, setConfirming] = useState<'approve' | 'reject' | null>(null);
    const [acting, setActing] = useState<boolean>(false);
    const [stores, setStores] = useState<Store[]>([]);
    const [selectedStore, setSelectedStore] = useState<string>('');
    const [itemLookup, setItemLookup] = useState<Record<string, Item>>({});
    const [stockLotIds, setStockLotIds] = useState<{ [key: string]: string | null }>({});

    useEffect(() => {
        const fetchRequisition = async () => {
            try {
                if (id) {
                    const response = await RequisitionService.getRequisitionById(id);
                    setRequisition(response);
                }
            } catch (err) {
                toast.showApiError(err, 'Failed to fetch requisition details');
            } finally {
                setLoading(false);
            }
        };

        fetchRequisition();
    }, [id]);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const storeList = await getStoresByInstitutionId();
                setStores(storeList.content);
            } catch (err) {
                toast.showApiError(err, 'Failed to fetch stores');
            }
        };

        fetchStores();
    }, []);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const res = await ItemService.getItemsByInstitution(0, 500);
                setItemLookup(Object.fromEntries((res.content || []).map(i => [i.id as string, i])));
            } catch (err) {
                // Non-fatal — lot pickers simply stay disabled when lookup fails.
            }
        };

        fetchItems();
    }, []);

    const handleApprovedQtyChange = (detailId: string, value: string) => {
        setApprovedInputs(prev => ({
            ...prev,
            [detailId]: value
        }));
    };

    const handleStoreChange = (id: string) => {
        setSelectedStore(id);
        // Clear the store error when a store is selected
        if (id) {
            setStoreError(false);
        }
    };

    const handleStockLotChange = (detailId: string, lotId: string | null) => {
        setStockLotIds(prev => ({ ...prev, [detailId]: lotId }));
    };

    const handleApprove = async () => {
        try {
            if (!requisition || !requisition.details) return;

            if (!selectedStore) {
                // Set store error state instead of just general error
                setStoreError(true);
                return;
            }

            const approvalDetails: ApprovalDetail[] = [];
            for (const detail of requisition.details) {
                const raw = approvedInputs[detail.id!];
                // Blank keeps the requested quantity; 0 declines the line; anything else is taken as typed.
                const qty = raw === undefined || raw.trim() === '' ? detail.requiredQty : Number(raw);
                if (!Number.isInteger(qty) || qty < 0 || qty > detail.requiredQty) {
                    toast.showError(`Approved quantity for ${detail.itemName} must be a whole number from 0 to ${detail.requiredQty}`);
                    setConfirming(null);
                    return;
                }
                approvalDetails.push({
                    id: detail.id!,
                    approvedQty: qty,
                    itemId: detail.itemId,
                    storeId: selectedStore,
                    stockLotId: stockLotIds[detail.id!] ?? null
                });
            }

            setActing(true);
            await RequisitionService.approveRequisition(requisition.id!, approvalDetails);
            toast.showSuccess(`Requisition ${requisition.refNo} approved`);
            navigate('/pending-approval-requisitions');
        } catch (err) {
            toast.showApiError(err, 'Failed to approve requisition');
        } finally {
            setActing(false);
            setConfirming(null);
        }
    };

    const handleReject = async (reason: string) => {
        try {
            if (!requisition?.id) return;
            setActing(true);
            await RequisitionService.rejectRequisition(requisition.id, reason);
            toast.showSuccess(`Requisition ${requisition.refNo} rejected`);
            navigate('/pending-approval-requisitions');
        } catch (err) {
            toast.showApiError(err, 'Failed to reject requisition');
        } finally {
            setActing(false);
            setConfirming(null);
        }
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (!requisition) return <Typography>Requisition not found</Typography>;

    return (
        <Box sx={{ p: 3 }} data-help-page="store.approveReq.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Pending Approval Requisition Details</Typography>
                <Stack direction="row" spacing={2}>
                    <Button
                        data-help="store.approveReq.approve"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (!selectedStore) { setStoreError(true); return; }
                            setConfirming('approve');
                        }}
                    >
                        Approve
                    </Button>
                    <Button
                        data-help="store.approveReq.reject"
                        variant="contained"
                        color="error"
                        onClick={() => setConfirming('reject')}
                    >
                        Reject
                    </Button>
                </Stack>
            </Box>

            <Paper sx={{ mb: 3, p: 2 }} data-help-section="store.approveReq.header">
                <Typography variant="subtitle1">Reference No: {requisition.refNo}</Typography>
                <Typography variant="subtitle1">Status: {requisition.status ? REQUISITION_STATUS_LABELS[requisition.status] : '-'}</Typography>
                <Typography variant="subtitle1">Date: {requisition.date ? formatDate(requisition.date) : '-'}</Typography>
                <Typography variant="subtitle1">Requested By: {requisition.requisitionByName}</Typography>
                {requisition.remarks && (
                    <Typography variant="subtitle1" sx={{ whiteSpace: 'pre-wrap' }}>Purpose / remarks: {requisition.remarks}</Typography>
                )}
            </Paper>

            <SearchableSelect<Store>
                options={stores}
                value={selectedStore}
                onChange={handleStoreChange}
                getOptionLabel={store => store.storeName}
                data-help="store.approveReq.field.store" label="Select Store"
                error={storeError}
                helperText={storeError ? 'Please select a store before approving' : undefined}
                sx={{ mb: 3, flex: '1 1 260px', maxWidth: 300 }}
            />

            <TableContainer component={Paper} data-help-section="store.approveReq.lines">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="store.reqLine.col.item">Item Name</TableCell>
                            <TableCell data-help="store.reqLine.col.required" align="right">Required Quantity</TableCell>
                            <TableCell data-help="store.reqLine.col.approveInput" align="right">Approved Quantity</TableCell>
                            <TableCell data-help="store.reqLine.col.lot">Lot</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requisition.details?.map((detail) => {
                            const item = itemLookup[detail.itemId];
                            const lotTracked = item?.lotTracked === true;
                            return (
                                <TableRow key={detail.id}>
                                    <TableCell>{detail.itemName}</TableCell>
                                    <TableCell align="right">{detail.requiredQty}</TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            type="number"
                                            size="small"
                                            data-help="store.approveReq.field.approvedQty" value={approvedInputs[detail.id!] ?? String(detail.requiredQty)}
                                            onChange={(e) => handleApprovedQtyChange(detail.id!, e.target.value)}
                                            helperText={approvedInputs[detail.id!] === '0' ? 'Line declined' : undefined}
                                            inputProps={{ min: 0, max: detail.requiredQty, step: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ minWidth: { sm: 220 } }}>
                                        {lotTracked && (
                                            <StockLotPicker
                                                data-help="store.reqLine.field.lot"
                                                storeId={selectedStore || undefined}
                                                itemId={detail.itemId}
                                                value={stockLotIds[detail.id!] ?? null}
                                                onChange={(lotId) => handleStockLotChange(detail.id!, lotId)}
                                                disabled={!selectedStore}
                                                refuseExpired   /* F6a: an expired lot cannot be issued */
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfirmDialog
                open={confirming === 'approve'}
                title="Approve this requisition?"
                message="Approving records the approved quantities and the selected store as the issuing store. Nothing is reserved or deducted now; stock only leaves the store when the requisition is issued, and availability is checked again then. A quantity of 0 declines that line."
                confirmLabel="Approve"
                confirmColor="primary"
                submitting={acting}
                onCancel={() => setConfirming(null)}
                onConfirm={() => handleApprove()}
            />
            <RejectReasonDialog
                open={confirming === 'reject'}
                submitting={acting}
                onCancel={() => setConfirming(null)}
                onConfirm={handleReject}
            />
        </Box>
    );
};

export default PendingApprovalRequisitionDetails;
